import {
  buildCheckpointReference,
  buildInitialMappings,
  clearDependency,
  createEmptyDependency,
  DEPENDENT_CONTROL_TYPES,
  equalsIgnoreCase,
  findCheckpointByReference,
  findMappingKey,
  flattenCheckpoints,
  includesIgnoreCase,
  isUsedAsTrigger,
  orderSectionsByIndex,
  rewriteDependencyReferences,
  rewriteDependencyReferencePrefix,
  syncMappingsOnOptionRemove,
  syncMappingsOnTriggerOptionAdd,
  syncMappingsOnTriggerOptionRemove,
  TRIGGER_CONTROL_TYPES,
} from '@/utils/checkpointDependency'

export { orderSectionsByIndex }

function getOrderedCheckpoint(
  sections: any[],
  sectionIndex: number,
  subSectionIndex: number,
  checkpointIndex: number
) {
  return flattenCheckpoints(sections).find(
    (cp) =>
      cp.sectionIndex === sectionIndex &&
      cp.subIndex === subSectionIndex &&
      cp.checkpointIndex === checkpointIndex
  )
}

export function toggleDependsOn(
  sections: any[],
  sectionIndex: number,
  subSectionIndex: number,
  checkpointIndex: number,
  enabled: boolean
): { sections: any[]; warning?: string } {
  const flat = getOrderedCheckpoint(
    sections,
    sectionIndex,
    subSectionIndex,
    checkpointIndex
  )
  const checkpoint = flat?.checkpoint
  if (!checkpoint) return { sections }

  if (!checkpoint.options) checkpoint.options = {}

  if (enabled) {
    if (isUsedAsTrigger(sections, flat.reference)) {
      return { sections }
    }
    checkpoint.options.dependency = createEmptyDependency()
  } else {
    clearDependency(checkpoint)
  }

  return { sections }
}

export function changeDependencyTrigger(
  sections: any[],
  sectionIndex: number,
  subSectionIndex: number,
  checkpointIndex: number,
  reference: string
): { sections: any[]; error?: string } {
  const flat = getOrderedCheckpoint(
    sections,
    sectionIndex,
    subSectionIndex,
    checkpointIndex
  )
  const checkpoint = flat?.checkpoint
  if (!checkpoint) return { sections }

  const trigger = findCheckpointByReference(sections, reference)
  if (trigger?.checkpoint?.options?.dependency) {
    return {
      sections,
      error: 'A checkpoint with dependency cannot be used as a trigger.',
    }
  }

  if (!checkpoint.options) checkpoint.options = {}
  checkpoint.options.dependency = {
    reference,
    mappings: buildInitialMappings(
      trigger?.data_source || [],
      checkpoint.data_source || []
    ),
  }

  return { sections }
}

export function toggleMappedOption(
  sections: any[],
  sectionIndex: number,
  subSectionIndex: number,
  checkpointIndex: number,
  triggerValue: string,
  optionValue: string,
  checked: boolean
) {
  const flat = getOrderedCheckpoint(
    sections,
    sectionIndex,
    subSectionIndex,
    checkpointIndex
  )
  const dependency = flat?.checkpoint?.options?.dependency
  if (!dependency) return sections

  const mappingKey =
    findMappingKey(dependency.mappings, triggerValue) || triggerValue
  const current = dependency.mappings?.[mappingKey] || []
  dependency.mappings = {
    ...dependency.mappings,
    [mappingKey]: checked
      ? includesIgnoreCase(current, optionValue)
        ? current
        : [...current, optionValue]
      : current.filter((v) => !equalsIgnoreCase(v, optionValue)),
  }

  return sections
}

export function setAllMappedOptions(
  sections: any[],
  sectionIndex: number,
  subSectionIndex: number,
  checkpointIndex: number,
  triggerValue: string,
  selectAll: boolean
) {
  const flat = getOrderedCheckpoint(
    sections,
    sectionIndex,
    subSectionIndex,
    checkpointIndex
  )
  const checkpoint = flat?.checkpoint
  const dependency = checkpoint?.options?.dependency
  if (!dependency) return sections

  const mappingKey =
    findMappingKey(dependency.mappings, triggerValue) || triggerValue
  dependency.mappings = {
    ...dependency.mappings,
    [mappingKey]: selectAll
      ? (checkpoint.data_source || []).map((d) => d.value)
      : [],
  }

  return sections
}

export function syncDependencyAfterDataSourceAdd(
  sections: any[],
  checkpoint: any,
  addedValue: string
) {
  const flat = flattenCheckpoints(sections).find(
    (item) => item.checkpoint === checkpoint
  )
  const reference = flat?.reference
  if (
    !reference ||
    !addedValue ||
    !TRIGGER_CONTROL_TYPES.includes(checkpoint.control_type)
  ) {
    return sections
  }

  flattenCheckpoints(sections).forEach((item) => {
    const dep = item.checkpoint?.options?.dependency
    if (dep?.reference !== reference) return
    item.checkpoint.options.dependency.mappings =
      syncMappingsOnTriggerOptionAdd(
        dep.mappings,
        addedValue,
        item.checkpoint.data_source || []
      )
  })

  return sections
}

export function syncDependencyAfterDataSourceRemove(
  sections: any[],
  checkpoint: any,
  removedValue: string
) {
  if (!removedValue) return sections

  if (checkpoint?.options?.dependency) {
    checkpoint.options.dependency.mappings = syncMappingsOnOptionRemove(
      checkpoint.options.dependency.mappings,
      removedValue
    )
  }

  const flat = flattenCheckpoints(sections).find(
    (item) => item.checkpoint === checkpoint
  )
  const reference = flat?.reference
  if (!reference || !TRIGGER_CONTROL_TYPES.includes(checkpoint.control_type)) {
    return sections
  }

  flattenCheckpoints(sections).forEach((item) => {
    const dep = item.checkpoint?.options?.dependency
    if (dep?.reference !== reference) return
    item.checkpoint.options.dependency.mappings =
      syncMappingsOnTriggerOptionRemove(dep.mappings, removedValue)
  })

  return sections
}

export function syncDependencyAfterControlTypeChange(
  sections: any[],
  checkpoint: any,
  nextControlType: string,
  previousReference?: string
) {
  if (
    !(DEPENDENT_CONTROL_TYPES as readonly string[]).includes(nextControlType) &&
    checkpoint?.options?.dependency
  ) {
    clearDependency(checkpoint)
  }

  if (
    previousReference &&
    !TRIGGER_CONTROL_TYPES.includes(nextControlType as any)
  ) {
    clearDependentsByReference(sections, previousReference)
  }

  return sections
}

function clearDependentsByReference(sections: any[], reference: string) {
  flattenCheckpoints(sections).forEach((item) => {
    if (item.checkpoint?.options?.dependency?.reference === reference) {
      clearDependency(item.checkpoint)
    }
  })
}

export function syncDependencyAfterCheckpointDelete(
  sections: any[],
  removedReference?: string
) {
  if (!removedReference) return sections
  clearDependentsByReference(sections, removedReference)
  return sections
}

export function syncDependencyAfterCheckpointRename(
  sections: any[],
  previousReference: string,
  nextReference: string
) {
  rewriteDependencyReferences(sections, previousReference, nextReference)
  return sections
}

export function syncDependencyAfterPathPrefixRename(
  sections: any[],
  previousPrefix: string,
  nextPrefix: string
) {
  rewriteDependencyReferencePrefix(sections, previousPrefix, nextPrefix)
  return sections
}

export function getCheckpointReferenceAt(
  sections: any[],
  sectionIndex: number,
  subSectionIndex: number,
  checkpointIndex: number
): string | null {
  return (
    getOrderedCheckpoint(
      sections,
      sectionIndex,
      subSectionIndex,
      checkpointIndex
    )?.reference || null
  )
}

export function buildReferenceFromParts(
  sectionName: string,
  subSectionName: string,
  checkpointName: string
) {
  return buildCheckpointReference(sectionName, subSectionName, checkpointName)
}
